import * as React from "react";
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { CgDanger } from "react-icons/cg";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import IconComponent from "../common/IconComponent";
import countryList from "../../_contstants/countryCodes.json";

const delay = 300;

const CountryCodeField = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    value,
    onChange,
    textField = "label",
    wrapperStyle,
    wrapperClassName,
    name,
    ref,
    astrike = false,
    ...others
  } = fieldRenderProps;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  interface CountryOption {
    value: string;
    label: string;
    image: string;
  }

  const options = React.useMemo(
    () =>
      countryList.map((e) => ({
        value: e.dialCode,
        label: e.name,
        image: e.flag,
      })),
    []
  );

  const [data, setData] = React.useState<CountryOption[]>(options);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const filterData = (filter: FilterDescriptor) => {
    return filterBy(options, filter);
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setData(
        filterData({
          field: "label",
          operator: "contains",
          ignoreCase: true,
          value: event.filter?.value || "",
        })
      );
    }, delay);
  };

  const handleSelect = (event: any) => {
    const selectedValue = event.target.value;
    onChange(selectedValue);
  };

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const item = itemProps.dataItem as CountryOption;
    const itemChildren = (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {item.image && (
          <img
            src={item.image}
            alt={`${item.label} flag`}
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
        )}
        <span>{item.label}</span>
        <span style={{ color: "#888" }}>({item.value})</span>
      </span>
    );
    return React.cloneElement(li, { ...li.props }, itemChildren);
  };

  // const selectedOption = options.find((option) => option.value === value);

  // const valueRender = (rendering: React.ReactElement<HTMLSpanElement>) => {
  //   if (!selectedOption || !selectedOption.image) {
  //     return rendering;
  //   }
  //   return (
  //     <span
  //       style={{
  //         display: "flex",
  //         alignItems: "center",
  //         width: "100%",
  //         paddingLeft: 5,
  //       }}
  //     >
  //       <img
  //         src={selectedOption.image}
  //         alt={`${selectedOption.label} flag`}
  //         style={{ width: 24, height: 24, objectFit: "contain" }}
  //       />
  //       <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
  //         {selectedOption.value}
  //       </span>
  //     </span>
  //   );
  // };

  const selectedOption =
    options.find((option) => option.value === value) || null;

  return (
    <FieldWrapper
      style={wrapperStyle}
      className={`${wrapperClassName} position-relative`}
    >
      <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
        {label} {astrike && <span style={{ color: "red" }}>*</span>}
      </Label>
      {/* {selectedOption && selectedOption.image && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            paddingLeft: "5px",
            position: "absolute",
            zIndex: "2",
            top: "24px",
            left: "2px",
            background: "white",
            width: "54%",
          }}
        >
          <img
            src={selectedOption?.image}
            alt={`${selectedOption?.label} flag`}
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
          <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
            {selectedOption?.value}
          </span>
        </span>
      )} */}
      <ComboBox
        ariaLabelledBy={labelId}
        size="large"
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={ref}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
        data={data}
        name={name}
        value={selectedOption || null}
        filterable
        onFilterChange={filterChange}
        textField={textField}
        defaultValue={value}
        onChange={handleSelect}
        itemRender={itemRender}
        // valueRender={valueRender}
        popupSettings={{ width: 350 }}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && (
        <Error className="d-flex justify-content-end" id={errorId}>
          <span
            className="d-flex align-items-center"
            style={{
              background: "#DF4957",
              color: "white",
              padding: "1px 8px 1px 5px",
              borderRadius: 10,
              gap: 5,
            }}
          >
            <IconComponent icon={CgDanger} /> {validationMessage}
          </span>
        </Error>
      )}
    </FieldWrapper>
  );
};

export default CountryCodeField;
